// src/services/licensing.js - License Management & Feature Gating
// Handles license validation, tier detection, and feature access control
// SECURITY: Enterprise-grade tier verification with cryptographic integrity checks

/**
 * LICENSE TIERS (4-Tier System)
 * NEW TIERS:
 * - explorer: Free tier - 30-day history, 3 experiments, no AI
 * - believer: $9/month - Unlimited history, 10 experiments, 50 AI calls/day
 * - guardian: $25/month - Unlimited everything, 500 AI calls/day
 * - lifetime: $299 one-time - All features unlimited forever
 *
 * LEGACY TIERS (still supported):
 * - free: Same as explorer
 * - pro: Same as believer
 * - local: Same as guardian
 */

import {
  generateDeviceFingerprint,
  secureStorageWrite,
  secureStorageRead,
  isDebugModeAllowed,
  isProduction,
  sanitizeTier,
  licenseValidationRateLimiter,
} from './security.js';

import { deduplicatedFetch } from '../utils/requestDeduplication.js';
import { isExperimentActive } from '../utils/helpers.js';
import { isTauriApp } from '../utils/tauriHelper';

// NOTE: Do NOT import ../utils/licenseSyncHelpers.js here.
// That module imports functions from this file, and a static import here creates an ESM circular
// dependency that can crash at runtime ("Cannot access 'X' before initialization"), especially
// in production/minified builds (observed in Tauri when calling local LLM).
async function syncLicenseTierFromServer() {
  try {
    const { default: pocketbaseClient } = await import('./pocketbase.js');

    // Not authenticated -> return local cached tier view (no mutation).
    if (!pocketbaseClient?.authStore?.isValid) {
      const localTier = normalizeTierName(localStorage.getItem('thryveloop_tier') || 'free');
      return { tier: localTier, source: 'local', synced: false };
    }

    const token = pocketbaseClient.authStore.token;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    let res;
    try {
      res = await fetch(`${pocketbaseClient.baseUrl}/api/sync-license`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    const text = await res.text();
    if (!res.ok) {
      throw new Error(text || `PocketBase request failed (${res.status})`);
    }

    const data = text ? JSON.parse(text) : {};
    const normalizedTier = normalizeTierName(data.tier || 'free');
    return {
      tier: normalizedTier,
      source: 'server',
      synced: true,
      expiresAt: data.expiresAt,
      status: data.status,
    };
  } catch (error) {
    const localTier = normalizeTierName(localStorage.getItem('thryveloop_tier') || 'free');
    return { tier: localTier, source: 'local', synced: false, error: error?.message || String(error) };
  }
}

// Storage keys
const LICENSE_KEY = 'thryveloop_license_key';
const LICENSE_DATA = 'thryveloop_license_data';
const TIER_KEY = 'thryveloop_tier';
const TIER_SECURE_KEY = 'thryveloop_tier_secure'; // Cryptographically signed tier
const TIER_OWNER_KEY = 'thryveloop_tier_owner';
const DEVICE_FP_KEY = 'thryveloop_device_fp'; // Device fingerprint cache

/**
 * Feature definitions by tier
 */
export const FEATURES = Object.freeze({
  // Core features (always available)
  BASIC_TRACKING: 'basic_tracking',
  BASIC_EXPERIMENTS: 'basic_experiments',
  BASIC_ANALYTICS: 'basic_analytics',
  WEEKLY_REVIEW: 'weekly_review',
  SESSION_FOCUS_MODE: 'session_focus_mode',
  RECOVERY_FLOW: 'recovery_flow',
  WEEKLY_NARRATIVE: 'weekly_narrative',
  COMPARE_WEEKS: 'compare_weeks',
  GLOBAL_SEARCH: 'global_search',

  // Paid-tier features (Believer / Guardian / Lifetime)
  UNLIMITED_HISTORY: 'unlimited_history',
  UNLIMITED_EXPERIMENTS: 'unlimited_experiments',
  EXPERIMENT_TEMPLATES: 'experiment_templates',
  SMART_REMINDERS: 'smart_reminders',
  BASIC_CORRELATIONS: 'basic_correlations',
  AI_INSIGHTS: 'ai_insights',
  TRIPLE_CHECK: 'triple_check',
  MAGIC_WINDOWS: 'magic_windows',
  EXPORT_DATA: 'export_data',
  CLOUD_SYNC: 'cloud_sync',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  ANALYTICS_PACKS: 'analytics_packs',
  AUTOMATION_RULES: 'automation_rules',
  SCHEDULED_REPORTS: 'scheduled_reports',
  DECISION_QUALITY: 'decision_quality',
  ENERGY_PREDICTION: 'energy_prediction',
  EXPERIMENT_COACH: 'experiment_coach',
  CONTEXT_BUDGET_METER: 'context_budget_meter',
  EXPERIMENT_MILESTONE_SHARING: 'experiment_milestone_sharing',

  // Desktop Lifetime additions (requires Tauri desktop + Lifetime tier)
  OFFLINE_REVIEW_SESSIONS: 'offline_review_sessions',
  LOCAL_ONLY_ANALYTICS: 'local_only_analytics',
  ENHANCED_CONFLICT_TOOLS: 'enhanced_conflict_tools',
  LOCAL_BACKUPS: 'local_backups',
});

/**
 * Tier feature matrix (frozen to prevent tampering)
 * NEW 4-TIER SYSTEM:
 * - explorer (FREE): Limited features, 30-day history, 3 experiments, NO AI
 * - believer ($9/month): Unlimited observations, 10 experiments, AI enabled 
 * - guardian ($25/month): Unlimited, 500 AI calls/day, AI enabled 
 * - lifetime ($299): All features unlimited, AI enabled 
 *
 * AI_INSIGHTS, EXPERIMENT_COACH, MAGIC_WINDOWS are PAID FEATURES ONLY
 */
const TIER_FEATURES = Object.freeze({
  explorer: Object.freeze([
    // Explorer (FREE) - Basic features only, NO AI
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA, // Can export data
    // NOTE: NO AI_INSIGHTS, NO EXPERIMENT_COACH, NO AI_COACH
  ]),
  believer: Object.freeze([
    // Believer ($9/month) - All features with AI 
    // Core features
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.WEEKLY_NARRATIVE,
    FEATURES.COMPARE_WEEKS,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA,
    // Paid-tier features
    FEATURES.UNLIMITED_HISTORY,
    FEATURES.UNLIMITED_EXPERIMENTS,
    FEATURES.EXPERIMENT_TEMPLATES,
    FEATURES.SMART_REMINDERS,
    FEATURES.BASIC_CORRELATIONS,
    FEATURES.AI_INSIGHTS,           //  AI Coach available
    FEATURES.EXPERIMENT_COACH,      //  AI Coach available
    FEATURES.TRIPLE_CHECK,
    FEATURES.MAGIC_WINDOWS,
    FEATURES.CLOUD_SYNC,
    FEATURES.ADVANCED_ANALYTICS,
    FEATURES.DECISION_QUALITY,
    FEATURES.ENERGY_PREDICTION,
    FEATURES.CONTEXT_BUDGET_METER,
    FEATURES.EXPERIMENT_MILESTONE_SHARING,
  ]),
  guardian: Object.freeze([
    // Guardian ($25/month) - All features with higher AI limits 
    // Core features
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.WEEKLY_NARRATIVE,
    FEATURES.COMPARE_WEEKS,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA,
    // Paid-tier features
    FEATURES.UNLIMITED_HISTORY,
    FEATURES.UNLIMITED_EXPERIMENTS,
    FEATURES.EXPERIMENT_TEMPLATES,
    FEATURES.SMART_REMINDERS,
    FEATURES.BASIC_CORRELATIONS,
    FEATURES.AI_INSIGHTS,           //  AI Coach available
    FEATURES.EXPERIMENT_COACH,      //  AI Coach available
    FEATURES.TRIPLE_CHECK,
    FEATURES.MAGIC_WINDOWS,
    FEATURES.CLOUD_SYNC,
    FEATURES.ADVANCED_ANALYTICS,
    FEATURES.ANALYTICS_PACKS,
    FEATURES.AUTOMATION_RULES,
    FEATURES.SCHEDULED_REPORTS,
    FEATURES.DECISION_QUALITY,
    FEATURES.ENERGY_PREDICTION,
    FEATURES.CONTEXT_BUDGET_METER,
    FEATURES.EXPERIMENT_MILESTONE_SHARING,
  ]),
  lifetime: Object.freeze([
    // Lifetime ($299) - All features unlimited 
    // Core features
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.WEEKLY_NARRATIVE,
    FEATURES.COMPARE_WEEKS,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA,
    // Paid-tier features
    FEATURES.UNLIMITED_HISTORY,
    FEATURES.UNLIMITED_EXPERIMENTS,
    FEATURES.EXPERIMENT_TEMPLATES,
    FEATURES.SMART_REMINDERS,
    FEATURES.BASIC_CORRELATIONS,
    FEATURES.AI_INSIGHTS,           //  AI Coach available
    FEATURES.EXPERIMENT_COACH,      //  AI Coach available
    FEATURES.TRIPLE_CHECK,
    FEATURES.MAGIC_WINDOWS,
    FEATURES.CLOUD_SYNC,
    FEATURES.ADVANCED_ANALYTICS,
    FEATURES.ANALYTICS_PACKS,
    FEATURES.AUTOMATION_RULES,
    FEATURES.SCHEDULED_REPORTS,
    FEATURES.DECISION_QUALITY,
    FEATURES.ENERGY_PREDICTION,
    FEATURES.OFFLINE_REVIEW_SESSIONS,
    FEATURES.LOCAL_ONLY_ANALYTICS,
    FEATURES.ENHANCED_CONFLICT_TOOLS,
    FEATURES.LOCAL_BACKUPS,
    FEATURES.CONTEXT_BUDGET_METER,
    FEATURES.EXPERIMENT_MILESTONE_SHARING,
  ]),
  // Legacy support for old tier names (maps to new names)
  free: Object.freeze([
    // Legacy: Same as explorer (NO AI)
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA,
  ]),
  pro: Object.freeze([
    // Legacy: Same as believer (AI enabled) 
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.WEEKLY_NARRATIVE,
    FEATURES.COMPARE_WEEKS,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA,
    FEATURES.UNLIMITED_HISTORY,
    FEATURES.UNLIMITED_EXPERIMENTS,
    FEATURES.EXPERIMENT_TEMPLATES,
    FEATURES.SMART_REMINDERS,
    FEATURES.BASIC_CORRELATIONS,
    FEATURES.AI_INSIGHTS,           //  AI Coach available
    FEATURES.EXPERIMENT_COACH,      //  AI Coach available
    FEATURES.TRIPLE_CHECK,
    FEATURES.MAGIC_WINDOWS,
    FEATURES.CLOUD_SYNC,
    FEATURES.ADVANCED_ANALYTICS,
    FEATURES.DECISION_QUALITY,
    FEATURES.ENERGY_PREDICTION,
    FEATURES.CONTEXT_BUDGET_METER,
    FEATURES.EXPERIMENT_MILESTONE_SHARING,
  ]),
  local: Object.freeze([
    // Legacy: Same as guardian (AI enabled) 
    FEATURES.BASIC_TRACKING,
    FEATURES.BASIC_EXPERIMENTS,
    FEATURES.BASIC_ANALYTICS,
    FEATURES.WEEKLY_REVIEW,
    FEATURES.SESSION_FOCUS_MODE,
    FEATURES.RECOVERY_FLOW,
    FEATURES.WEEKLY_NARRATIVE,
    FEATURES.COMPARE_WEEKS,
    FEATURES.GLOBAL_SEARCH,
    FEATURES.EXPORT_DATA,
    FEATURES.UNLIMITED_HISTORY,
    FEATURES.UNLIMITED_EXPERIMENTS,
    FEATURES.EXPERIMENT_TEMPLATES,
    FEATURES.SMART_REMINDERS,
    FEATURES.BASIC_CORRELATIONS,
    FEATURES.AI_INSIGHTS,           //  AI Coach available
    FEATURES.EXPERIMENT_COACH,      //  AI Coach available
    FEATURES.TRIPLE_CHECK,
    FEATURES.MAGIC_WINDOWS,
    FEATURES.CLOUD_SYNC,
    FEATURES.ADVANCED_ANALYTICS,
    FEATURES.ANALYTICS_PACKS,
    FEATURES.AUTOMATION_RULES,
    FEATURES.SCHEDULED_REPORTS,
    FEATURES.DECISION_QUALITY,
    FEATURES.ENERGY_PREDICTION,
    FEATURES.CONTEXT_BUDGET_METER,
    FEATURES.EXPERIMENT_MILESTONE_SHARING,
  ]),
});

/**
 * Tier limits and pricing
 */
export const TIER_CONFIG = Object.freeze({
  explorer: {
    name: 'Explorer',
    price: 0,
    billing: 'free',
    observationHistory: 30, // days
    maxExperiments: 3,
    aiEnabled: false,
    aiCallsPerDay: 0,
    cloudSync: false,
  },
  believer: {
    name: 'Believer',
    price: 9,
    billing: 'month',
    observationHistory: -1, // unlimited
    maxExperiments: 10,
    aiEnabled: true,
    aiCallsPerDay: 50,
    cloudSync: true,
  },
  guardian: {
    name: 'Guardian',
    price: 25,
    billing: 'month',
    observationHistory: -1, // unlimited
    maxExperiments: -1, // unlimited
    aiEnabled: true,
    aiCallsPerDay: 500,
    cloudSync: true,
  },
  lifetime: {
    name: 'Lifetime',
    price: 299,
    billing: 'once',
    observationHistory: -1, // unlimited
    maxExperiments: -1, // unlimited
    aiEnabled: true,
    aiCallsPerDay: -1, // unlimited
    cloudSync: true,
  },
});

/**
 * Free tier limits (for Explorer tier - frozen to prevent tampering)
 */
export const FREE_TIER_LIMITS = Object.freeze({
  HISTORY_DAYS: 30,
  MAX_EXPERIMENTS: 3,
  ANALYTICS_DAYS: 7,
});

/**
 * Get or generate device fingerprint
 * Cached to avoid regenerating on every call
 * @returns {Promise<string>}
 */
const getDeviceFingerprint = async () => {
  try {
    // Check cache first
    let fingerprint = localStorage.getItem(DEVICE_FP_KEY);

    if (!fingerprint) {
      // Generate new fingerprint
      fingerprint = await generateDeviceFingerprint();
      localStorage.setItem(DEVICE_FP_KEY, fingerprint);
      console.log(' Device fingerprint generated');
    }

    return fingerprint;
  } catch (error) {
    console.error('Error getting device fingerprint:', error);
    // Fallback to timestamp-based ID
    return `fallback-${Date.now()}`;
  }
};

/**
 * Valid tier names (old and new)
 */
const VALID_TIERS = Object.freeze([
  'free', 'pro', 'local', 'lifestyle', // Legacy tier names
  'explorer', 'believer', 'guardian', 'lifetime' // New tier names
]);

const LEGACY_TIER_MAP = Object.freeze({
  free: 'explorer',
  pro: 'believer',
  local: 'lifetime',
  lifestyle: 'lifetime'
});

const TIER_PRIORITY = Object.freeze({
  explorer: 0,
  believer: 1,
  guardian: 2,
  lifetime: 3,
});
const ORDERED_TIERS = Object.freeze(['explorer', 'believer', 'guardian', 'lifetime']);

export const normalizeTierName = (tier) => {
  const value = (tier || '').toString().trim().toLowerCase();
  if (!value) {
    return 'explorer';
  }

  if (LEGACY_TIER_MAP[value]) {
    return LEGACY_TIER_MAP[value];
  }

  if (['explorer', 'believer', 'guardian', 'lifetime'].includes(value)) {
    return value;
  }

  // Unknown tier defaults to explorer
  return 'explorer';
};

const ACTIVE_LICENSE_STATUSES = Object.freeze(['active', 'trialing', 'paid', 'renewing', 'lifetime']);

const isServerLicenseActive = (license) => {
  if (!license) {
    return false;
  }

  const status = (license.subscription_status || '').toString().toLowerCase();
  const expiresAt = license.expires_at ? new Date(license.expires_at).getTime() : null;
  const normalizedTier = normalizeTierName(license.tier);
  const now = Date.now();

  if (normalizedTier === 'lifetime') {
    return true;
  }

  if (ACTIVE_LICENSE_STATUSES.includes(status)) {
    if (!expiresAt) {
      return true;
    }
    return expiresAt > now;
  }

  if (status === 'cancelled' && expiresAt) {
    return expiresAt > now;
  }

  return false;
};

const getBestTierFromLicenses = (licenses = []) => {
  let bestTier = 'explorer';

  licenses.forEach((license) => {
    const normalizedTier = normalizeTierName(license?.tier);
    if (!isServerLicenseActive(license)) {
      return;
    }

    if ((TIER_PRIORITY[normalizedTier] || 0) > (TIER_PRIORITY[bestTier] || 0)) {
      bestTier = normalizedTier;
    }
  });

  return bestTier;
};

/**
 * Get current license tier with security verification
 * SECURITY: For authenticated users, gets tier from PocketBase user_licenses
 * For unauthenticated users, uses localStorage
 * Supports both old (free, pro, local) and new (explorer, believer, guardian, lifetime) tier names
 * @returns {Promise<string>}
 */
export const getCurrentTier = async () => {
  try {
    // Debug mode check (development only)
    const debugTier = process.env.REACT_APP_DEBUG_TIER;
    if (debugTier && VALID_TIERS.includes(debugTier)) {
      if (isDebugModeAllowed()) {
        console.warn(`  DEBUG MODE: Using tier = ${debugTier}`);
        return debugTier;
      }
    }

    //  PRIMARY SOURCE OF TRUTH: PocketBase /api/user/tier endpoint
    // ALWAYS query server - NO cache fallback
    const pb = (await import('./pocketbase.js')).default;
    if (!pb.authStore.isValid) {
      console.log('[getCurrentTier] User not authenticated, returning explorer');
      return 'explorer';
    }

    const res = await pb.send('/api/user/tier', { method: 'GET', $autoCancel: false });
    const tier = normalizeTierName(res?.tier || 'explorer');
    console.log('[getCurrentTier] Server returned tier:', tier);

    // Update local cache AFTER server confirms
    await setLicenseTier(tier);
    return tier;

  } catch (error) {
    // If server fetch fails, keep the best local client tier instead of hard-downgrading to explorer.
    // This prevents transient network/auth hydration blips from blocking paid AI access.
    console.error('[getCurrentTier] Server fetch failed:', error.message);
    try {
      const cachedTier = normalizeTierName(await getCachedTierForClient());
      if (cachedTier && cachedTier !== 'explorer') {
        console.warn('[getCurrentTier] Falling back to cached paid tier:', cachedTier);
        return cachedTier;
      }
    } catch (_) {}
    console.error('[getCurrentTier] Returning explorer fallback');
    return 'explorer';
  }
};

/**
 * Get current license tier (synchronous version for backwards compatibility)
 *   WARNING: ALWAYS returns 'explorer' unless tier is explicitly set via setLicenseTier
 * This ensures tier is ALWAYS synced from server via LicenseContext
 * DO NOT use localStorage as fallback - tier must come from server only
 * @returns {string}
 */
export const getCurrentTierSync = () => {
  try {
    // Debug mode check (development only)
    const debugTier = process.env.REACT_APP_DEBUG_TIER;
    if (debugTier && VALID_TIERS.includes(debugTier)) {
      if (isDebugModeAllowed()) {
        console.warn('[getCurrentTierSync] Using DEBUG tier:', debugTier);
        return normalizeTierName(debugTier);
      }
    }

    // SERVER-ONLY ARCHITECTURE: Return explorer as default
    // Tier MUST be loaded via LicenseContext which queries /api/user/tier
    // This prevents stale localStorage values from being used
    console.log('[getCurrentTierSync] Returning explorer (tier must load from server via LicenseContext)');
    return 'explorer';
  } catch (error) {
    console.error('[getCurrentTierSync] Error:', error);
    return 'explorer';
  }
};

/**
 *  SECURITY FIX: Get stored tier with priority protection
 * Returns current tier with priority-based downgrade prevention
 * @returns {Promise<string>} - Current tier or 'explorer' as fallback
 */
const getStoredTierWithPriority = async () => {
  try {
    const { default: pocketbaseClient } = await import('./pocketbase.js');
    const decodeTokenUserId = (token = '') => {
      const raw = String(token || '').trim();
      if (!raw) return '';
      const parts = raw.split('.');
      if (parts.length < 2) return '';
      try {
        const payloadRaw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payloadRaw.padEnd(payloadRaw.length + ((4 - (payloadRaw.length % 4)) % 4), '=');
        const decoded = (typeof atob === 'function')
          ? atob(padded)
          : (typeof Buffer !== 'undefined' ? Buffer.from(padded, 'base64').toString('utf8') : '');
        if (!decoded) return '';
        const payload = JSON.parse(decoded);
        return String(payload?.id || payload?.sub || '').trim();
      } catch (_) {
        return '';
      }
    };

    const modelUserId = pocketbaseClient.authStore.model?.id || '';
    const tokenUserId = decodeTokenUserId(pocketbaseClient.authStore.token || '');
    const currentUserId = modelUserId || tokenUserId || 'anonymous';
    const storedOwner = localStorage.getItem(TIER_OWNER_KEY);

    if (
      storedOwner &&
      currentUserId &&
      currentUserId !== 'anonymous' &&
      storedOwner !== currentUserId
    ) {
      console.log('[licensing] Tier owner mismatch, resetting to explorer', { storedOwner, currentUserId });
      return 'explorer';
    }

    // Try to read secure tier first
    const fingerprint = await getDeviceFingerprint();
    const secureResult = await secureStorageRead(TIER_SECURE_KEY, fingerprint);
    if (
      secureResult &&
      secureResult.value &&
      ['explorer', 'believer', 'guardian', 'lifetime'].includes(secureResult.value)
    ) {
      return secureResult.value;
    }

    // Fallback to legacy storage
    const legacyTier = localStorage.getItem(TIER_KEY);
    if (legacyTier && ['explorer', 'believer', 'guardian', 'lifetime'].includes(legacyTier)) {
      return legacyTier;
    }

    return 'explorer';
  } catch (error) {
    console.warn('[licensing] Error reading stored tier:', error?.message);
    return 'explorer';
  }
};

/**
 * Get the cached tier that the client UI should treat as current.
 *
 * This is intentionally separate from getCurrentTier/getCurrentTierSync:
 * - getCurrentTier() always goes to the server and may temporarily return "explorer"
 *   during sync or if the endpoint is unavailable.
 * - getCurrentTierSync() always returns "explorer" to force LicenseContext to be
 *   the single source of truth.
 *
 * For UX-sensitive paths (like AI usage limits) we want to align with the tier that
 * has already been securely stored by setLicenseTier/LicenseContext, while still
 * benefiting from downgrade protection.
 */
export const getCachedTierForClient = async () => {
  return getStoredTierWithPriority();
};

/**
 *  TIER DOWNGRADE PROTECTION
 * Ensures a user's tier never falls to a lower tier
 * This is critical protection against network/API failures returning explorer as default
 * @param {string} newTier - The tier being set
 * @returns {Promise<string>} - The safe tier (never lower than current)
 */
export const ensureTierNeverDowngrades = async (newTier) => {
  try {
    const currentStored = await getStoredTierWithPriority();
    const currentPriority = TIER_PRIORITY[currentStored] || 0;
    const newPriority = TIER_PRIORITY[newTier] || 0;

    if (newPriority < currentPriority) {
      console.warn('[licensing]   DOWNGRADE BLOCKED:', {
        from: currentStored,
        to: newTier,
        currentPriority,
        newPriority,
        action: 'KEEPING_HIGHER_TIER'
      });
      return currentStored; // Keep the higher tier
    }

    // Safe to upgrade or keep same tier
    console.log('[licensing]  Tier transition safe:', {
      from: currentStored,
      to: newTier,
      action: newPriority > currentPriority ? 'UPGRADE' : 'SAME'
    });
    return newTier;
  } catch (error) {
    console.error('[licensing] Error in ensureTierNeverDowngrades:', error);
    // On error, return the new tier (optimistic)
    return newTier;
  }
};

/**
 * Set license tier with cryptographic integrity protection.
 * SECURITY: Stores tier with HMAC signature using device fingerprint.
 * Supports both old (free, pro, local) and new (explorer, believer, guardian, lifetime) tier names.
 * By default, allows legitimate downgrades coming from server-side billing state.
 * @param {string} tier - Any valid tier name
 * @param {{ allowDowngrade?: boolean }} options
 */
export const setLicenseTier = async (tier, options = {}) => {
  try {
    const { allowDowngrade = true } = options;
    let normalizedTier = normalizeTierName(sanitizeTier(tier));

    // Optional strict mode for defensive local-only paths.
    if (!allowDowngrade) {
      normalizedTier = await ensureTierNeverDowngrades(normalizedTier);
    }

    const { default: pocketbaseClient } = await import('./pocketbase.js');
    const decodeTokenUserId = (token = '') => {
      const raw = String(token || '').trim();
      if (!raw) return '';
      const parts = raw.split('.');
      if (parts.length < 2) return '';
      try {
        const payloadRaw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payloadRaw.padEnd(payloadRaw.length + ((4 - (payloadRaw.length % 4)) % 4), '=');
        const decoded = (typeof atob === 'function')
          ? atob(padded)
          : (typeof Buffer !== 'undefined' ? Buffer.from(padded, 'base64').toString('utf8') : '');
        if (!decoded) return '';
        const payload = JSON.parse(decoded);
        return String(payload?.id || payload?.sub || '').trim();
      } catch (_) {
        return '';
      }
    };
    const ownerId = pocketbaseClient.authStore.model?.id || decodeTokenUserId(pocketbaseClient.authStore.token || '') || 'anonymous';
    localStorage.setItem(TIER_OWNER_KEY, ownerId);

    // Get device fingerprint
    const fingerprint = await getDeviceFingerprint();

    //  SECURITY: Store with HMAC signature
    await secureStorageWrite(TIER_SECURE_KEY, normalizedTier, fingerprint);

    // Also update legacy storage for backwards compatibility
    localStorage.setItem(TIER_KEY, normalizedTier);

    console.log(` License tier set to: ${normalizedTier} (with integrity protection)`);
  } catch (error) {
    console.error('Error setting tier:', error);
    throw error;
  }
};

/**
 * Get stored license key
 * @returns {string | null}
 */
export const getLicenseKey = () => {
  try {
    return localStorage.getItem(LICENSE_KEY);
  } catch (error) {
    console.error('Error reading license key:', error);
    return null;
  }
};

/**
 * Save license key
 * @param {string} key
 */
export const saveLicenseKey = (key) => {
  try {
    localStorage.setItem(LICENSE_KEY, key);
  } catch (error) {
    console.error('Error saving license key:', error);
  }
};

/**
 * Get cached license data
 * @returns {object | null}
 */
export const getCachedLicenseData = () => {
  try {
    const data = localStorage.getItem(LICENSE_DATA);
    if (!data) return null;
    const parsed = JSON.parse(data);
    // Check if cache is expired (24 hours)
    const cacheAge = Date.now() - parsed.timestamp;
    if (cacheAge > 24 * 60 * 60 * 1000) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Error reading cached license:', error);
    return null;
  }
};

/**
 * Cache license data
 * @param {object} data
 */
export const cacheLicenseData = (data) => {
  try {
    localStorage.setItem(LICENSE_DATA, JSON.stringify({
      ...data,
      timestamp: Date.now(),
    }));
  } catch (error) {
    console.error('Error caching license data:', error);
  }
};

/**
 * Clear license data (logout/reset)
 * SECURITY: Clears all license data including secure storage
 */
export const clearLicenseData = async () => {
  try {
    localStorage.removeItem(LICENSE_KEY);
    localStorage.removeItem(LICENSE_DATA);
    localStorage.removeItem(TIER_KEY);
    localStorage.removeItem(TIER_SECURE_KEY);
    localStorage.removeItem(TIER_OWNER_KEY);
    // Note: Keep device fingerprint for re-activation
    console.log(' License data cleared');
  } catch (error) {
    console.error('Error clearing license data:', error);
  }
};

/**
 * Check if a feature is enabled for a specific tier
 * @param {string} tier - Tier name (legacy or new)
 * @param {string} feature - Feature constant
 * @returns {boolean}
 */
export const isFeatureEnabledForTier = (tier, feature) => {
  const normalized = normalizeTierName(tier);
  const enabledFeatures = TIER_FEATURES[normalized] || TIER_FEATURES.explorer;
  return enabledFeatures.includes(feature);
};

/**
 * Resolve the minimum tier required to access a feature.
 * This prevents UI gating labels/copy from moving away from the source-of-truth matrix.
 * @param {string} feature - Feature constant from FEATURES
 * @returns {'explorer' | 'believer' | 'guardian' | 'lifetime'}
 */
export const getMinimumTierForFeature = (feature) => {
  const matchedTier = ORDERED_TIERS.find((tierName) => {
    const enabledFeatures = TIER_FEATURES[tierName] || [];
    return enabledFeatures.includes(feature);
  });
  return matchedTier || 'explorer';
};

/**
 * Check if a feature is enabled for current tier (async version)
 * @param {string} feature - Feature constant from FEATURES
 * @returns {Promise<boolean>}
 */
export const isFeatureEnabled = async (feature) => {
  const tier = await getCurrentTier();
  return isFeatureEnabledForTier(tier, feature);
};

/**
 * Check if a feature is enabled for current tier (synchronous version)
 * WARNING: Less secure, use async version when possible
 * @param {string} feature - Feature constant from FEATURES
 * @returns {boolean}
 */
export const isFeatureEnabledSync = (feature) => {
  const tier = getCurrentTierSync();
  return isFeatureEnabledForTier(tier, feature);
};

/**
 * Check if user has paid tier (async version)
 * Paid tiers: pro, local (legacy), believer, guardian, lifetime
 * @returns {Promise<boolean>}
 */
export const isPaidUser = async () => {
  const tier = await getCurrentTier();
  const normalized = normalizeTierName(tier);
  return ['believer', 'guardian', 'lifetime'].includes(normalized);
};

/**
 * Check if user has paid tier (synchronous version)
 * Paid tiers: pro, local (legacy), believer, guardian, lifetime
 * @returns {boolean}
 */
export const isPaidUserSync = () => {
  const tier = getCurrentTierSync();
  const normalized = normalizeTierName(tier);
  return ['believer', 'guardian', 'lifetime'].includes(normalized);
};

/**
 * Get tier display info
 * @param {'free' | 'pro' | 'local'} tier
 * @returns {object}
 */
export const getTierInfo = (tier) => {
  const normalized = normalizeTierName(tier);
  const info = {
    explorer: {
      name: 'Explorer',
      icon: 'globe-2',
      color: '#6B8482',
      description: 'Basic tracking and experiments',
      limits: `${FREE_TIER_LIMITS.HISTORY_DAYS} days history, ${FREE_TIER_LIMITS.MAX_EXPERIMENTS} experiments`,
    },
    believer: {
      name: 'Believer',
      icon: 'star',
      color: '#5E8C6A',
      description: 'All features + AI insights',
      price: '$9/month',
    },
    guardian: {
      name: 'Guardian',
      icon: 'shield',
      color: '#4A90A4',
      description: 'All features + premium AI (500 calls/day)',
      price: '$25/month',
    },
    lifetime: {
      name: 'Lifetime',
      icon: 'infinity',
      color: '#D4AF37',
      description: 'All features unlimited forever + offline AI',
      price: '$299 one-time',
    },
  };
  return info[normalized] || info.explorer;
};

/**
 * Get upgrade URL for Lemon Squeezy
 *
 * SETUP INSTRUCTIONS:
 * 1. Create products in Lemon Squeezy dashboard
 * 2. Get checkout URLs from each product ("Get checkout link")
 * 3. Set environment variables in Vercel:
 *    - REACT_APP_LEMON_SQUEEZY_PRO_URL
 *    - REACT_APP_LEMON_SQUEEZY_LOCAL_URL
 *
 * URL format: https://your-store.lemonsqueezy.com/checkout/buy/VARIANT_ID
 *
 * @param {'pro' | 'local'} targetTier
 * @returns {string}
 */
export const getUpgradeURL = (targetTier = 'believer', email = '', userId = '') => {
  const urls = {
    believer: process.env.REACT_APP_LEMON_SQUEEZY_PRO_URL ||
         'https://thryveloop.lemonsqueezy.com/checkout/buy/REPLACE_WITH_BELIEVER_VARIANT_ID',
    guardian: process.env.REACT_APP_LEMON_SQUEEZY_GUARDIAN_URL ||
         'https://thryveloop.lemonsqueezy.com/checkout/buy/REPLACE_WITH_GUARDIAN_VARIANT_ID',
    lifetime: process.env.REACT_APP_LEMON_SQUEEZY_LIFETIME_URL ||
         'https://thryveloop.lemonsqueezy.com/checkout/buy/REPLACE_WITH_LIFETIME_VARIANT_ID'
  };

  // Legacy tier mapping
  const normalizedTier = (() => {
    if (targetTier === 'pro') return 'believer';
    if (targetTier === 'local') return 'lifetime';
    if (targetTier === 'free') return 'explorer';
    return targetTier;
  })();

  let url = urls[normalizedTier] || urls.believer;

  //  FIX: Ensure URL uses lemonsqueezy.com domain, not thryveloop.com
  // The environment variables should be set to https://thryveloop.lemonsqueezy.com/checkout/buy/XXX
  // but if they're incorrectly set to https://www.thryveloop.com/checkout/buy/XXX, fix it here
  if (url.includes('www.thryveloop.com/checkout/buy/')) {
    // Extract the variant ID and reconstruct the URL correctly
    const match = url.match(/checkout\/buy\/([a-zA-Z0-9_-]+)/);
    if (match && match[1]) {
      const variantId = match[1];
      url = `https://thryveloop.lemonsqueezy.com/checkout/buy/${variantId}`;
      console.warn(
        `  Fixed incorrect Lemon Squeezy URL domain.\n` +
        `Environment variable should be: https://thryveloop.lemonsqueezy.com/checkout/buy/${variantId}\n` +
        `But was set to: ${urls[targetTier]}\n` +
        `Using corrected URL: ${url}`
      );
    }
  } else if (url.includes('thryveloop.com/checkout/buy/') && !url.includes('lemonsqueezy.com')) {
    // Catch any other thryveloop.com variant
    const match = url.match(/checkout\/buy\/([a-zA-Z0-9_-]+)/);
    if (match && match[1]) {
      const variantId = match[1];
      url = `https://thryveloop.lemonsqueezy.com/checkout/buy/${variantId}`;
      console.warn(
        `  Fixed incorrect Lemon Squeezy URL domain.\n` +
        `Environment variable should be: https://thryveloop.lemonsqueezy.com/checkout/buy/${variantId}\n` +
        `But was set to: ${urls[targetTier]}\n` +
        `Using corrected URL: ${url}`
      );
    }
  }

  // Warn if using placeholder URLs
  if (url.includes('REPLACE_WITH')) {
    console.warn(
      '  Using placeholder Lemon Squeezy URL. Set REACT_APP_LEMON_SQUEEZY_PRO_URL, ' +
      'REACT_APP_LEMON_SQUEEZY_GUARDIAN_URL, and REACT_APP_LEMON_SQUEEZY_LIFETIME_URL ' +
      'environment variables with your actual checkout URLs.'
    );
  }

  // Force checkout to the logged-in email if provided
  const userEmail = (email || '').trim();
  const sep = url.includes('?') ? '&' : '?';

  if (userEmail) {
    url = `${url}${sep}checkout[email]=${encodeURIComponent(userEmail)}`;
  }

  // Log the final URL for debugging
  console.log(` Opening Lemon Squeezy checkout for ${targetTier} tier: ${url} (email=${userEmail || 'none'})`);

  return url;
};

const validateLemonCheckoutUrl = (checkoutUrl = '') => {
  const raw = String(checkoutUrl || '').trim();
  if (!raw || raw.includes('REPLACE_WITH')) {
    return { ok: false, error: 'Invalid Lemon Squeezy checkout URL' };
  }
  try {
    const parsed = new URL(raw);
    const host = parsed.host.toLowerCase();
    const path = parsed.pathname || '';
    if (parsed.protocol !== 'https:') {
      return { ok: false, error: 'Invalid Lemon Squeezy checkout URL' };
    }
    if (host !== 'thryveloop.lemonsqueezy.com') {
      return { ok: false, error: 'Invalid Lemon Squeezy checkout URL' };
    }
    if (!/^\/checkout\/buy\/[a-zA-Z0-9_-]+\/?$/.test(path)) {
      return { ok: false, error: 'Invalid Lemon Squeezy checkout URL' };
    }
    return { ok: true, error: null };
  } catch (_) {
    return { ok: false, error: 'Invalid Lemon Squeezy checkout URL' };
  }
};

const normalizeCheckoutReturnOrigin = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return 'https://thryveloop.com';

  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const parsed = new URL(candidate);
    if (!/^https?:$/.test(parsed.protocol)) {
      return 'https://thryveloop.com';
    }
    return `${parsed.protocol}//${parsed.host}`;
  } catch (_) {
    return 'https://thryveloop.com';
  }
};

const CHECKOUT_RETURN_ORIGIN_ALLOWLIST = new Set([
  'thryveloop.com',
  'www.thryveloop.com',
  'staging.thryveloop.com',
  'localhost:3000',
  '127.0.0.1:3000'
]);

const resolveCheckoutReturnOrigin = () => {
  const configuredOrigin = normalizeCheckoutReturnOrigin(
    process.env.REACT_APP_CHECKOUT_RETURN_ORIGIN || process.env.REACT_APP_PUBLIC_APP_URL || 'https://thryveloop.com'
  );

  try {
    if (typeof window === 'undefined' || !window.location) {
      return configuredOrigin;
    }

    const isTauriRuntime = isTauriApp();
    if (isTauriRuntime) {
      return configuredOrigin;
    }

    const origin = String(window.location.origin || '').trim();
    if (!origin) return configuredOrigin;
    const parsed = new URL(origin);
    if (!/^https?:$/.test(parsed.protocol)) return configuredOrigin;
    if (!CHECKOUT_RETURN_ORIGIN_ALLOWLIST.has(parsed.host.toLowerCase())) {
      return configuredOrigin;
    }
    return `${parsed.protocol}//${parsed.host}`;
  } catch (_) {
    return configuredOrigin;
  }
};

/**
 * Build success/cancel return URLs for hosted checkout sessions.
 */
const buildCheckoutReturnUrls = (targetTier = 'believer') => {
  const normalizedTier = normalizeTierName(targetTier || 'believer');
  const origin = resolveCheckoutReturnOrigin();
  return {
    successUrl: `${origin}/?checkout=success&tier=${encodeURIComponent(normalizedTier)}`,
    cancelUrl: `${origin}/?checkout=cancel&tier=${encodeURIComponent(normalizedTier)}`
  };
};

export const CHECKOUT_PENDING_KEY = 'thryveloop_checkout_return_pending_v1';
const CHECKOUT_PENDING_MAX_AGE_MS = 1000 * 60 * 60 * 6;

const loadPocketBaseClient = async () => {
  const module = await import('./pocketbase.js');
  return module.default || module.pb || null;
};

const loadPbSync = async () => {
  const module = await import('./pbSync.js');
  return module.default || null;
};

const preserveWebSyncKeyForCheckout = async () => {
  if (isTauriApp()) {
    return;
  }

  try {
    const sync = await loadPbSync();
    if (typeof sync?.setRememberDevicePreference === 'function') {
      sync.setRememberDevicePreference(true);
    }
    if (typeof sync?.waitForEncryption === 'function') {
      await sync.waitForEncryption();
    }
    if (typeof sync?.persistEncryptionKeyToSession === 'function') {
      await sync.persistEncryptionKeyToSession();
    }
  } catch (error) {
    console.warn('[Licensing] Failed to persist sync key before checkout redirect:', error?.message || error);
  }
};

const hasAuthenticatedCheckoutSession = async () => {
  try {
    const client = await loadPocketBaseClient();
    return Boolean(client?.authStore?.isValid);
  } catch (_) {
    return false;
  }
};

const normalizeOptionalPendingTier = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return normalizeTierName(raw);
};

export const readPendingCheckoutState = () => {
  try {
    const raw = localStorage.getItem(CHECKOUT_PENDING_KEY) || '';
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

export const writePendingCheckoutState = (state = {}) => {
  try {
    const previous = readPendingCheckoutState() || {};
    const normalizedTier = normalizeOptionalPendingTier(state.tier || previous.tier || '');
    const normalizedStatus = (state.status || previous.status || 'initiated').toString().trim().toLowerCase();
    const next = {
      ...previous,
      ...state,
      tier: normalizedTier || '',
      status: normalizedStatus || 'initiated',
      updatedAt: Date.now()
    };
    if (!next.startedAt) {
      next.startedAt = Date.now();
    }
    localStorage.setItem(CHECKOUT_PENDING_KEY, JSON.stringify(next));
    return next;
  } catch (_) {
    return null;
  }
};

export const clearPendingCheckoutState = () => {
  try {
    localStorage.removeItem(CHECKOUT_PENDING_KEY);
  } catch (_) {
    // ignore
  }
};

export const isPendingCheckoutFresh = (state, maxAgeMs = CHECKOUT_PENDING_MAX_AGE_MS) => {
  if (!state || typeof state !== 'object') return false;
  const startedAt = Number(state.startedAt || state.updatedAt || 0);
  if (!Number.isFinite(startedAt) || startedAt <= 0) return false;
  return Date.now() - startedAt <= maxAgeMs;
};

const isNumericId = (value = '') => /^\d+$/.test(String(value || '').trim());

const isHostedCheckoutEnabledByDefault = () => false;

/**
 * Open Lemon Squeezy checkout in the current tab.
 * Default strategy is always direct Lemon checkout URL (minimal path, fewer moving parts).
 * Hosted checkout is legacy-only and must be explicitly requested per-call.
 *
 * @param {object} options
 * @param {string} options.targetTier
 * @param {string} options.email
 * @param {string} options.userId
 * @param {boolean} options.preferHostedCheckout - Hosted checkout preference (defaults false).
 * @param {boolean} options.strictHostedCheckout - If hosted was attempted and fails, return error instead of redirect fallback.
 * @param {(url: string) => void} options.navigate
 * @returns {Promise<{ok: boolean, checkoutUrl: string, redirecting: boolean, usedHostedCheckout: boolean, error: string|null}>}
 */
export const openUpgradeCheckout = async ({
  targetTier = 'believer',
  email = '',
  userId = '',
  preferHostedCheckout = isHostedCheckoutEnabledByDefault(),
  strictHostedCheckout = false,
  navigate = (url) => {
    if (typeof window !== 'undefined' && window.location?.assign) {
      window.location.assign(url);
    }
  }
} = {}) => {
  const normalizedTier = normalizeTierName(targetTier || 'believer');
  const userEmail = (email || '').trim();
  const pbUserId = (userId || '').trim();
  const staticCheckoutUrl = getUpgradeURL(normalizedTier, userEmail, pbUserId);
  let checkoutUrl = staticCheckoutUrl;
  let usedHostedCheckout = false;
  let hostedCheckoutAttempted = false;
  let hostedCheckoutError = null;
  let resolvedAuthEmail = '';
  let resolvedAuthUserId = '';
  if (preferHostedCheckout) {
    try {
      const { default: pocketbaseClient } = await import('./pocketbase.js');
      if (pocketbaseClient?.authStore?.isValid) {
        hostedCheckoutAttempted = true;
        const authEmail = (pocketbaseClient.authStore.model?.email || '').trim();
        const authUserId = (pocketbaseClient.authStore.model?.id || '').trim();
        resolvedAuthEmail = authEmail;
        resolvedAuthUserId = authUserId;
        const { successUrl, cancelUrl } = buildCheckoutReturnUrls(normalizedTier);
        const checkoutBody = {
          targetTier: normalizedTier,
          variantId: null,
          email: userEmail || authEmail || null,
          successUrl,
          cancelUrl,
          customData: {
            pb_user_id: pbUserId || authUserId || null,
            pb_user_email: userEmail || authEmail || null,
            target_tier: normalizedTier
          }
        };
        const tierVariantEnv = {
          believer: process.env.REACT_APP_LEMON_SQUEEZY_VARIANT_ID_BELIEVER || '',
          guardian: process.env.REACT_APP_LEMON_SQUEEZY_VARIANT_ID_GUARDIAN || '',
          lifetime: process.env.REACT_APP_LEMON_SQUEEZY_VARIANT_ID_LIFETIME || ''
        };
        const resolvedVariantId = (tierVariantEnv[normalizedTier] || '').toString().trim();
        if (isNumericId(resolvedVariantId)) {
          checkoutBody.variantId = resolvedVariantId;
        }

        // eMind-style single hosted checkout route: one endpoint, minimal surface area.
        const checkoutEndpoints = ['/api/lemonsqueezy/checkout-v2'];

        for (let i = 0; i < checkoutEndpoints.length; i += 1) {
          const endpoint = checkoutEndpoints[i];
          try {
            const response = await pocketbaseClient.send(endpoint, {
              method: 'POST',
              body: checkoutBody,
              query: {
                targetTier: normalizedTier,
                target_tier: normalizedTier,
                tier: normalizedTier,
                email: userEmail || authEmail || '',
                pb_user_id: pbUserId || authUserId || '',
                pb_user_email: userEmail || authEmail || '',
                successUrl,
                success_url: successUrl,
                variantId: checkoutBody.variantId || '',
                variant_id: checkoutBody.variantId || ''
              }
            });

            const hostedUrl =
              response?.checkoutUrl ||
              response?.url ||
              response?.data?.attributes?.url ||
              response?.data?.url ||
              '';

            if (hostedUrl) {
              checkoutUrl = String(hostedUrl);
              usedHostedCheckout = true;
              hostedCheckoutError = null;
              break;
            }
            hostedCheckoutError = 'Checkout session was created without a valid redirect URL.';
          } catch (endpointError) {
            const status = Number(endpointError?.status || endpointError?.response?.status || 0);
            const isLast = i === checkoutEndpoints.length - 1;
            if (!isLast && (status === 400 || status === 404 || status === 405 || status === 422)) {
              continue;
            }
            hostedCheckoutError = endpointError?.message || 'Failed to create hosted checkout session.';
            break;
          }
        }
      }
    } catch (error) {
      hostedCheckoutError = error?.message || 'Failed to create hosted checkout session.';
      console.warn('[Licensing] Hosted checkout session creation failed:', error);
    }
  }

  if (hostedCheckoutAttempted && !usedHostedCheckout) {
    if (strictHostedCheckout) {
      return {
        ok: false,
        checkoutUrl: '',
        redirecting: false,
        usedHostedCheckout: false,
        error: hostedCheckoutError || 'We could not open secure checkout. Please retry.'
      };
    }
    console.warn('[Licensing] Hosted checkout failed, falling back to direct checkout URL:', hostedCheckoutError);
  }

  const checkoutValidation = validateLemonCheckoutUrl(checkoutUrl);
  if (!checkoutValidation.ok) {
    clearPendingCheckoutState();
    return {
      ok: false,
      checkoutUrl: '',
      redirecting: false,
      usedHostedCheckout,
      error: checkoutValidation.error
    };
  }

  if (await hasAuthenticatedCheckoutSession()) {
    await preserveWebSyncKeyForCheckout();
  }

  try {
    writePendingCheckoutState({
      status: 'initiated',
      tier: normalizedTier,
      checkoutUrl,
      usedHostedCheckout,
      userId: pbUserId || resolvedAuthUserId || null,
      userEmail: userEmail || resolvedAuthEmail || null,
      source: usedHostedCheckout ? 'hosted_checkout' : 'direct_checkout'
    });
    navigate(checkoutUrl);
    return {
      ok: true,
      checkoutUrl,
      redirecting: true,
      usedHostedCheckout,
      error: null
    };
  } catch (error) {
    clearPendingCheckoutState();
    console.warn('[Licensing] Checkout navigation failed:', error);
    return {
      ok: false,
      checkoutUrl,
      redirecting: false,
      usedHostedCheckout,
      error: error?.message || 'Failed to navigate to checkout'
    };
  }
};

/**
 * Validate license and update tier
 * This should be called:
 * - On app startup
 * - When user enters license key
 * - Periodically (every 24 hours)
 *
 * SECURITY: Rate-limited to prevent brute force attacks
 *
 * @param {string} licenseKey - Optional license key to validate
 * @param {string} email - Optional user email for verification
 * @returns {Promise<object>}
 */
export const validateLicense = async (licenseKey = null, email = null) => {
  try {
    // Use provided key or stored key
    const key = (licenseKey || '').toString().trim() || getLicenseKey();

    // Get email from parameter or stored auth cache (avoid importing auth.js to prevent circular deps).
    let userEmail = email;
    if (!userEmail) {
      try {
        userEmail = localStorage.getItem('thryveloop_user_email') || '';
        userEmail = userEmail.toString().trim() || null;
      } catch (error) {
        // localStorage not available, continue without email
      }
    }

	    if (!key) {
	      // No local key: always trust server source of truth before falling back to free
	      try {
	        const server = await syncLicenseTierFromServer();
	        if (server?.tier) {
	          await setLicenseTier(server.tier);
	          return {
	            success: true,
            tier: server.tier,
            valid: true,
            message: 'Using server tier',
            server: true,
          };
        }
      } catch (serverErr) {
        console.warn('Server tier lookup failed, falling back to free', serverErr?.message || serverErr);
      }

      // Still nothing: free
      await setLicenseTier('free');
      return {
        success: true,
        tier: 'free',
        valid: true,
        message: 'Using free tier',
      };
    }

    //  SECURITY: Rate limiting to prevent brute force
    const rateLimitKey = `license_${key.substring(0, 8)}`;
    if (!licenseValidationRateLimiter.isAllowed(rateLimitKey)) {
      console.warn('  Rate limit exceeded for license validation');
      return {
        success: false,
        tier: 'free',
        valid: false,
        error: 'Too many validation attempts. Please try again in a minute.',
        rateLimited: true,
      };
    }

    // Do not trust cache for tier; always rely on server or explicit key

    // Validate with PocketBase (custom routes require manual auth token in header)
    let data;
    try {
      const { default: pocketbaseClient } = await import('./pocketbase.js');

      const token = pocketbaseClient.authStore.token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Only send device fingerprint in desktop (Tauri) runtime.
      // Browsers vary the fingerprint across userAgent/platform and can burn device slots unintentionally.
      const isTauriRuntime = isTauriApp();
      const deviceFingerprint = isTauriRuntime ? await getDeviceFingerprint() : undefined;

      const response = await fetch(`${pocketbaseClient.baseUrl}/api/validate-license`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`, // PocketBase requireAuth() reads this header
          'Content-Type': 'application/json',
          ...(key ? { 'X-License-Key': key } : {}),
        },
        body: JSON.stringify({
          licenseKey: key,
          email: userEmail,
          instanceName: userEmail || `web-${Date.now()}`,
          ...(deviceFingerprint ? { deviceFingerprint } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'License validation failed');
      }

      data = await response.json();
    } catch (pbError) {
      console.error('[Licensing] PocketBase validation failed:', pbError);
      throw new Error('License validation failed');
    }

    if (data.valid) {
      // Save license data
      saveLicenseKey(key);
      await setLicenseTier(data.tier);
      cacheLicenseData(data);

      // Reset rate limiter on successful validation
      licenseValidationRateLimiter.reset(rateLimitKey);

      return {
        success: true,
        valid: true,
        tier: data.tier,
        ...data,
      };
    } else {
      // Invalid license, revert to free
      await clearLicenseData();
      await setLicenseTier('free');

      return {
        success: false,
        valid: false,
        tier: 'free',
        message: data.message || 'Invalid license key',
      };
    }

  } catch (error) {
    console.error('License validation error:', error);

    // On error, fallback to free tier (no cache)
    await setLicenseTier('free');
    return {
      success: false,
      tier: 'free',
      valid: false,
      error: error.message,
      message: 'Validation failed, using free tier',
    };
  }
};

/**
 * Detect tier manipulation
 * Checks if the stored tier is valid based on license key presence
 * SECURITY: Enhanced detection with multiple checks
 * @returns {Promise<object>}
 */
const detectTierManipulation = async () => {
  const savedTier = localStorage.getItem(TIER_KEY);
  const normalizedSavedTier = normalizeTierName(savedTier || 'explorer');
  const licenseKey = getLicenseKey();

  // Get device fingerprint
  const fingerprint = await getDeviceFingerprint();

  // Check secure storage integrity
  const secureResult = await secureStorageRead(TIER_SECURE_KEY, fingerprint);

  // If secure storage shows tampering, that's a manipulation
  if (secureResult.tampered) {
    console.error(' TIER MANIPULATION DETECTED: Secure storage tampered');
    return { manipulated: true, tier: 'free', reason: 'tampered_storage' };
  }

  // Free tier without license = OK
  if (!licenseKey && normalizedSavedTier === 'explorer') {
    return { manipulated: false, tier: 'explorer' };
  }

  // Paid tier without license = MANIPULATION
  if (!licenseKey && ['believer', 'guardian', 'lifetime'].includes(normalizedSavedTier)) {
    console.error(' TIER MANIPULATION DETECTED: Paid tier without license key');
    return { manipulated: true, tier: 'explorer', reason: 'no_license' };
  }

  // Explorer tier with license = needs verification
  if (licenseKey && normalizedSavedTier === 'explorer') {
    return { manipulated: false, tier: 'explorer', needsVerification: true };
  }

  // Paid tier with license = OK (but should verify periodically)
  return { manipulated: false, tier: normalizedSavedTier, needsVerification: true };
};

/**
 * Verify license on startup with grace period
 * SECURITY: Enhanced manipulation detection and integrity checks
 * @returns {Promise<object>}
 */
export const verifyLicenseOnStartup = async () => {
  const LAST_VERIFIED_KEY = 'thryveloop_last_verified';

  try {
    //  Always try to pull the current tier from server (authoritative)
    try {
      const { default: pocketbaseClient } = await import('./pocketbase.js');
      const token = pocketbaseClient.authStore.token;
      if (token) {
        const res = await fetch(`${pocketbaseClient.baseUrl}/api/user/tier`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const serverTier = (await res.json()).tier;
          if (serverTier) {
            await setLicenseTier(serverTier);
            localStorage.setItem(LAST_VERIFIED_KEY, Date.now().toString());
            return {
              success: true,
              tier: serverTier,
              valid: true,
              source: 'server',
              synced: true,
            };
          }
        }
      }
    } catch (syncErr) {
      console.warn('Server tier fetch failed, continuing with cached/LS flow', syncErr.message);
    }

	    //  Try to sync from server FIRST (before checking for manipulation)
	    // This is important for users who purchased via webhook but don't have local license key yet
	    try {
	      console.log(' Syncing license from server...');
	      const serverSync = await syncLicenseTierFromServer();
	      console.log(' Server sync result:', serverSync);

	      if (serverSync.synced && serverSync.tier) {
	        console.log(' License synced from server:', serverSync.tier);
        await setLicenseTier(serverSync.tier);
        localStorage.setItem(LAST_VERIFIED_KEY, Date.now().toString());
        return {
          success: true,
          tier: serverSync.tier,
          valid: true,
          source: 'server',
          synced: true,
          expiresAt: serverSync.expiresAt,
          status: serverSync.status,
        };
      } else {
        console.log('  Server sync did not return valid tier:', serverSync);
      }
    } catch (syncError) {
      console.error(' Server sync failed:', syncError);
    }

    //  SECURITY: Now check for manipulation (after server sync attempt)
    const manipulationCheck = await detectTierManipulation();
    if (manipulationCheck.manipulated) {
      console.error(' Manipulation detected:', manipulationCheck.reason);
      await setLicenseTier('free');
      return {
        success: false,
        tier: 'free',
        manipulated: true,
        message: 'Tier manipulation detected. Reset to free tier.',
      };
    }

    const licenseKey = getLicenseKey();

    // No local key may be normal for webhook-managed subscriptions.
    // For authenticated users, do not downgrade on this branch.
    if (!licenseKey) {
      const { default: pocketbaseClient } = await import('./pocketbase.js');
      if (pocketbaseClient?.authStore?.isValid) {
        const cachedTier = await getCachedTierForClient();
        const safeTier = normalizeTierName(cachedTier || 'explorer');
        console.log('No local license key but authenticated - keeping cached tier:', safeTier);
        return {
          success: true,
          tier: safeTier,
          valid: true,
          source: 'cached_no_local_key',
          message: 'Authenticated user without local key; keeping cached tier',
        };
      }

      console.log('No local license key (unauthenticated) - defaulting to free tier');
      await setLicenseTier('free');
      return {
        success: true,
        tier: 'free',
        valid: true,
        message: 'Free tier - no license key',
      };
    }

    // Verify with backend
    console.log(' Verifying license with backend...');
    const result = await validateLicense();

    if (result.valid) {
      // Update last verified timestamp
      localStorage.setItem(LAST_VERIFIED_KEY, Date.now().toString());
      console.log(' License verified successfully');
      return result;
    } else {
      // License invalid - downgrade to free
      console.warn('  License validation failed, downgrading to free tier');
      await setLicenseTier('free');
      return {
        success: false,
        tier: 'free',
        valid: false,
        message: result.message || 'License validation failed',
      };
    }
  } catch (error) {
    console.error('Error verifying license on startup:', error);

    // Network error - fall back to last known tier without downgrading storage
    console.warn('  License verification failed - using last known tier (no cache)');
    const fallbackTier = normalizeTierName((await getCachedTierForClient()) || 'explorer');
    return {
      success: false,
      tier: fallbackTier,
      valid: false,
      offline: true,
      error: error.message,
    };
  }
};

/**
 * Initialize licensing on app startup
 * Should be called in AppV5.js componentDidMount/useEffect
 * SECURITY: Performs full security checks on startup
 * @returns {Promise<object>}
 */
export const initializeLicensing = async () => {
  try {
    console.log(' Initializing licensing system with security checks...');
    const result = await verifyLicenseOnStartup();
    console.log('License initialization result:', result);
    return result;
  } catch (error) {
    console.error('Error initializing licensing:', error);
    return {
      success: false,
      tier: 'free',
      error: error.message,
    };
  }
};

/**
 * Check if free tier limits are exceeded (async version)
 * @param {object} data - Data to check (observations, experiments, etc.)
 * @returns {Promise<object>}
 */
export const checkFreeTierLimits = async (data = {}, tierOverride = null) => {
  const rawTier = tierOverride || await getCurrentTier();
  const tier = normalizeTierName(rawTier);
  return evaluateTierLimits(tier, data);
};

/**
 * Check if free tier limits are exceeded (synchronous version)
 * Checks for both 'free' (legacy) and 'explorer' (new) tier names
 * @param {object} data - Data to check (observations, experiments, etc.)
 * @returns {object}
 */
export const checkFreeTierLimitsSync = (data = {}, tierOverride = null) => {
  const rawTier = tierOverride || getCurrentTierSync();
  const tier = normalizeTierName(rawTier);
  return evaluateTierLimits(tier, data);
};

const evaluateTierLimits = (tier, data = {}) => {
  const normalizedTier = normalizeTierName(tier);
  const tierConfig = TIER_CONFIG[normalizedTier] || {};
  const limits = {
    history: false,
    experiments: false,
    analytics: false,
  };

  // Explorer-only limits
  if (normalizedTier === 'explorer') {
    if (data.observations) {
      const thirtyDaysAgo = Date.now() - (FREE_TIER_LIMITS.HISTORY_DAYS * 24 * 60 * 60 * 1000);
      const oldestAllowed = data.observations.filter(obs => {
        const ts = new Date(obs.timestamp || obs.date || obs.createdAt || obs.created).getTime();
        return !Number.isNaN(ts) && ts >= thirtyDaysAgo;
      });
      limits.history = data.observations.length > oldestAllowed.length;
    }

    if (data.analyticsWindow) {
      const sevenDaysAgo = Date.now() - (FREE_TIER_LIMITS.ANALYTICS_DAYS * 24 * 60 * 60 * 1000);
      const hasOlderData = data.analyticsWindow.some(obs => {
        const ts = new Date(obs.timestamp || obs.date || obs.createdAt || obs.created).getTime();
        return !Number.isNaN(ts) && ts < sevenDaysAgo;
      });
      limits.analytics = hasOlderData;
    }
  }

  // Experiment limits (Explorer + Believer)
  if (data.experiments && typeof tierConfig.maxExperiments === 'number' && tierConfig.maxExperiments > -1) {
    const activeExperiments = data.experiments.filter(exp => isExperimentActive(exp));
    limits.experiments = activeExperiments.length >= tierConfig.maxExperiments;
  }

  const exceeded = Object.values(limits).some(Boolean);
  return { exceeded, limits, tier: normalizedTier };
};

/**
 * TESTING UTILITIES
 *  SECURITY: These functions are ONLY exposed in development mode
 * Production builds will BLOCK access to these utilities
 */

/**
 * Set tier directly for testing (bypasses license validation)
 *  SECURITY: Only works in development mode
 * Usage in browser console:
 *   window.ThryveLoopTesting.setDebugTier('explorer')   // Free tier
 *   window.ThryveLoopTesting.setDebugTier('believer')   // $9/month
 *   window.ThryveLoopTesting.setDebugTier('guardian')   // $25/month
 *   window.ThryveLoopTesting.setDebugTier('lifetime')   // $299 one-time
 *   window.ThryveLoopTesting.setDebugTier('free')       // Legacy free tier
 *   window.ThryveLoopTesting.setDebugTier('pro')        // Legacy pro tier
 *   window.ThryveLoopTesting.setDebugTier('local')      // Legacy local tier
 * @param {string} tier - Valid tier name
 */
export const setDebugTier = async (tier) => {
  //  SECURITY: Block in production
  if (!isDebugModeAllowed()) {
    console.error(' DEBUG MODE BLOCKED: This function is disabled in production');
    return false;
  }

  if (!VALID_TIERS.includes(tier)) {
    console.error(`Invalid tier: ${tier}. Valid tiers: ${VALID_TIERS.join(', ')}`);
    return false;
  }

  await setLicenseTier(tier);
  console.log(` DEBUG: Tier set to ${tier}. Refresh the page to apply.`);
  return true;
};

/**
 * Get current tier info for debugging
 *  SECURITY: Limited info in production
 */
export const getDebugInfo = async () => {
  const tier = await getCurrentTier();
  const licenseKey = getLicenseKey();
  // Base info (safe to show in production)
  const info = {
    currentTier: tier,
    hasLicenseKey: !!licenseKey,
    isProduction: isProduction(),
    debugModeAllowed: isDebugModeAllowed(),
  };

  // Extended info (only in development)
  if (isDebugModeAllowed()) {
    info.debugTierEnv = process.env.REACT_APP_DEBUG_TIER || 'not set';
    info.features = {
      aiInsights: await isFeatureEnabled(FEATURES.AI_INSIGHTS),
      tripleCheck: await isFeatureEnabled(FEATURES.TRIPLE_CHECK),
      magicWindows: await isFeatureEnabled(FEATURES.MAGIC_WINDOWS),
      unlimitedExperiments: await isFeatureEnabled(FEATURES.UNLIMITED_EXPERIMENTS),
    };
  }

  return info;
};

/**
 *  SECURITY: Expose testing utilities ONLY in development mode
 * Production builds will NOT have window.ThryveLoopTesting
 */
if (isDebugModeAllowed()) {
  if (typeof window !== 'undefined') {
    window.ThryveLoopTesting = {
      setDebugTier,
      getDebugInfo,
      setLicenseTier,
      getCurrentTier,
      getCurrentTierSync,
      clearLicenseData,
      FEATURES,
      isFeatureEnabled,
      isFeatureEnabledSync,
      isPaidUser,
      isPaidUserSync,
    };
    console.log(' Testing utilities available: window.ThryveLoopTesting');
    console.log('  WARNING: You are in development mode. Debug features are enabled.');
  }
} else {
  //  SECURITY: Log that testing utilities are blocked
  console.log(' Production mode: Debug features disabled');
}

// Freeze exports to prevent tampering
export default Object.freeze({
  FEATURES,
  FREE_TIER_LIMITS,
  // Async versions (secure, recommended)
  getCurrentTier,
  setLicenseTier,
  isFeatureEnabled,
  isFeatureEnabledForTier,
  isPaidUser,
  checkFreeTierLimits,
  // Sync versions (backwards compatibility, less secure)
  getCurrentTierSync,
  isFeatureEnabledSync,
  isPaidUserSync,
  checkFreeTierLimitsSync,
  // Core functions
  getTierInfo,
  getUpgradeURL,
  normalizeTierName,
  validateLicense,
  initializeLicensing,
  getLicenseKey,
  saveLicenseKey,
  clearLicenseData,
  // Testing (development only)
  setDebugTier,
  getDebugInfo,
});
